/**
 * HiBFF V3 - Three-Pillar Focus Picker
 * A 60-second guided flow that helps users choose what to work on,
 * then auto-suggests 1-3 pathways from a template library.
 * Feeds into the existing Pathways engine.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Heart, Briefcase, Smile, ArrowRight, ArrowLeft, Check, Sparkles, Target, Loader2, Pencil } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

// ─── PILLAR OPTIONS ─────────────────────────────────────────────────────────

const PILLAR_OPTIONS = {
  health: {
    label: 'Health',
    icon: Heart,
    color: 'pink',
    gradient: 'from-pink-500 to-rose-600',
    options: [
      { id: 'h1', text: 'Feeling less nervous in social situations' },
      { id: 'h2', text: 'Sleeping better before big days' },
      { id: 'h3', text: 'Spending a bit less time on my phone' },
      { id: 'h4', text: 'Taking better care of myself when I\'m stressed' },
    ],
  },
  wealth: {
    label: 'Wealth',
    icon: Briefcase,
    color: 'blue',
    gradient: 'from-blue-500 to-indigo-600',
    options: [
      { id: 'w1', text: 'Feeling more confident in class or meetings' },
      { id: 'w2', text: 'Speaking up with ideas' },
      { id: 'w3', text: 'Handling group projects or teamwork better' },
      { id: 'w4', text: 'Preparing for interviews or new opportunities' },
    ],
  },
  happiness: {
    label: 'Happiness',
    icon: Smile,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-600',
    options: [
      { id: 'j1', text: 'Making new friends (or deepening the ones I have)' },
      { id: 'j2', text: 'Handling disagreements without drama' },
      { id: 'j3', text: 'Feeling more like myself around people' },
      { id: 'j4', text: 'Finding communities or groups I enjoy' },
    ],
  },
};

// ─── PATHWAY TEMPLATES ──────────────────────────────────────────────────────

const PATHWAY_TEMPLATES = [
  {
    id: 'new_friends_basic',
    title: 'Small Steps to New Friends',
    skill_area: 'social_skills',
    context: 'home',
    pillars: ['happiness'],
    matches: ['j1', 'j3', 'j4'],
    steps: [
      'Say hi to one person you don\'t usually talk to this week.',
      'Ask one follow-up question in a conversation.',
      'Send one message to check in on someone you already know.',
      'Join or rejoin one group activity (online or offline).',
    ],
  },
  {
    id: 'speak_up',
    title: 'Speak Up Once a Day',
    skill_area: 'confidence',
    context: 'school',
    pillars: ['wealth'],
    matches: ['w1', 'w2'],
    steps: [
      'Ask one question in class or a meeting.',
      'Share one idea with a group, even if it feels small.',
      'Volunteer for a task or role you wouldn\'t normally take.',
    ],
  },
  {
    id: 'phone_down',
    title: 'Phone-Down Moments',
    skill_area: 'social_skills',
    context: 'home',
    pillars: ['health'],
    matches: ['h3', 'h4'],
    steps: [
      'Put your phone in another room for 30 minutes tonight.',
      'Have one full conversation without checking your phone.',
      'Replace one social media scroll session with a walk or stretch.',
    ],
  },
  {
    id: 'calm_confidence',
    title: 'Calmer Before Big Moments',
    skill_area: 'confidence',
    context: 'home',
    pillars: ['health'],
    matches: ['h1', 'h2'],
    steps: [
      'Write down what\'s making you nervous — just one sentence.',
      'Practise saying the first thing you\'d say in the situation, out loud.',
      'Do one small thing that makes you feel prepared (lay out clothes, review notes, etc.).',
      'Ask your Ava to help you rehearse the moment.',
    ],
  },
  {
    id: 'team_player',
    title: 'Team Player Week',
    skill_area: 'teamwork',
    context: 'school',
    pillars: ['wealth'],
    matches: ['w3', 'w4'],
    steps: [
      'Offer to help a teammate with one task.',
      'Ask someone what they think before sharing your own idea.',
      'Give one specific compliment about someone\'s contribution.',
    ],
  },
  {
    id: 'disagree_well',
    title: 'Disagree Without Drama',
    skill_area: 'conflict_handling',
    context: 'home',
    pillars: ['happiness'],
    matches: ['j2'],
    steps: [
      'When you disagree, say "I see it differently" instead of arguing.',
      'Ask "Can you help me understand your side?" once this week.',
      'Walk away from one argument that isn\'t worth having.',
    ],
  },
  {
    id: 'everyday_confidence',
    title: 'Everyday Confidence',
    skill_area: 'confidence',
    context: 'home',
    pillars: ['health', 'wealth', 'happiness'],
    matches: [],
    universal: true,
    steps: [
      'Make eye contact and smile at three people today.',
      'Start one conversation with someone you wouldn\'t normally talk to.',
      'Say something kind about yourself, out loud, at the end of the day.',
    ],
  },
];

function getSuggestedPathways(selectedIds) {
  const scored = PATHWAY_TEMPLATES.map(t => ({
    ...t,
    score: t.matches.filter(m => selectedIds.includes(m)).length + (t.universal ? 0.5 : 0),
  }));
  scored.sort((a, b) => b.score - a.score);
  const top = scored.filter(t => t.score > 0).slice(0, 2);
  if (top.length < 2) {
    const universal = scored.find(t => t.universal && !top.includes(t));
    if (universal) top.push(universal);
  }
  return top.slice(0, 3);
}

// ─── COMPONENT ──────────────────────────────────────────────────────────────

const FocusPicker = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [creating, setCreating] = useState(null);

  // Load previous picks
  useEffect(() => {
    apiClient.request('/api/users/me').then(u => {
      if (u.focus_tags?.length) setSelected(u.focus_tags);
    }).catch(() => {});
  }, []);

  const toggle = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(s => s !== id));
    } else if (selected.length < 3) {
      setSelected([...selected, id]);
    }
  };

  const handleNext = async () => {
    // Save focus to user profile
    const pillars = [];
    Object.entries(PILLAR_OPTIONS).forEach(([key, pillar]) => {
      if (pillar.options.some(o => selected.includes(o.id))) pillars.push(key);
    });

    try {
      await apiClient.request('/api/users/onboarding', {
        method: 'POST',
        body: JSON.stringify({ primary_modal: pillars[0] || 'happiness', first_goal: '', focus_tags: selected, pillars_selected: pillars }),
      });
    } catch (e) {}

    setSuggestions(getSuggestedPathways(selected));
    setStep(3);
  };

  const applyPathway = async (template, edit = false) => {
    setCreating(template.id);
    try {
      const pw = await apiClient.request('/api/pathways', {
        method: 'POST',
        body: JSON.stringify({ title: template.title, skill_area: template.skill_area, context: template.context }),
      });
      for (const label of template.steps) {
        await apiClient.request(`/api/pathways/${pw.pathway_id}/steps`, {
          method: 'POST', body: JSON.stringify({ label }),
        });
      }
      if (edit) {
        navigate(`/pathways/${pw.pathway_id}`);
      } else {
        await apiClient.request(`/api/pathways/${pw.pathway_id}/activate`, { method: 'POST' });
        navigate(`/pathways/${pw.pathway_id}`);
      }
    } catch (e) { console.error(e); }
    setCreating(null);
  };

  const selectedTexts = selected.map(id => {
    for (const p of Object.values(PILLAR_OPTIONS)) {
      const opt = p.options.find(o => o.id === id);
      if (opt) return opt.text;
    }
    return '';
  }).filter(Boolean);

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-3xl mx-auto" data-testid="focus-picker">

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1.5 rounded-full transition-all ${s <= step ? 'w-16 bg-purple-500' : 'w-10 bg-gray-200'}`} />
          ))}
        </div>

        {/* Step 1 — Intro */}
        {step === 1 && (
          <div className="text-center" data-testid="picker-step-1">
            <Sparkles size={40} className="mx-auto text-purple-500 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Let&apos;s pick what matters most right now</h1>
            <p className="text-gray-600 max-w-lg mx-auto mb-8">
              You don&apos;t have to fix everything at once. Just choose a few areas where you&apos;d like tiny wins over the next couple of weeks.
            </p>
            <div className="flex justify-center gap-4 mb-8">
              {Object.entries(PILLAR_OPTIONS).map(([key, pillar]) => {
                const Icon = pillar.icon;
                return (
                  <div key={key} className={`px-5 py-3 rounded-xl bg-${pillar.color}-50 flex items-center gap-2`}>
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${pillar.gradient} flex items-center justify-center`}>
                      <Icon size={16} className="text-white" />
                    </div>
                    <span className="font-medium text-gray-900">{pillar.label}</span>
                  </div>
                );
              })}
            </div>
            <button onClick={() => setStep(2)} className="px-8 py-4 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 flex items-center gap-2 mx-auto">
              Start <ArrowRight size={18} />
            </button>
          </div>
        )}

        {/* Step 2 — Pillar Cards */}
        {step === 2 && (
          <div data-testid="picker-step-2">
            <h2 className="text-xl font-bold text-gray-900 mb-1 text-center">What would you like to feel a bit better about?</h2>
            <p className="text-sm text-gray-500 text-center mb-6">Pick up to 3. You can always change these later.</p>

            <div className="space-y-4 mb-6">
              {Object.entries(PILLAR_OPTIONS).map(([key, pillar]) => {
                const Icon = pillar.icon;
                return (
                  <div key={key} className={`p-4 rounded-2xl bg-${pillar.color}-50 border border-${pillar.color}-200`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${pillar.gradient} flex items-center justify-center`}>
                        <Icon size={16} className="text-white" />
                      </div>
                      <span className="font-bold text-gray-900">{pillar.label}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {pillar.options.map(opt => {
                        const isSelected = selected.includes(opt.id);
                        const disabled = !isSelected && selected.length >= 3;
                        return (
                          <button
                            key={opt.id}
                            onClick={() => !disabled && toggle(opt.id)}
                            disabled={disabled}
                            className={`p-3 rounded-xl text-left text-sm transition-all flex items-start gap-2 ${
                              isSelected ? 'bg-white ring-2 ring-purple-400 text-gray-900' :
                              disabled ? 'bg-white/50 text-gray-400 cursor-not-allowed' :
                              'bg-white text-gray-700 hover:ring-1 hover:ring-gray-300'
                            }`}
                            data-testid={`option-${opt.id}`}
                          >
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${isSelected ? 'bg-purple-500 border-purple-500' : 'border-gray-300'}`}>
                              {isSelected && <Check size={12} className="text-white" />}
                            </div>
                            <span>{opt.text}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-gray-400 text-center mb-4">
              {selected.length === 0 ? 'Try picking at least one thing you\'d like to feel a bit better about. It can be small.' :
               selected.length < 3 ? `${selected.length} selected — three is plenty.` :
               '3 selected — nice set of focuses!'}
            </p>

            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl">Back</button>
              <button onClick={handleNext} disabled={selected.length === 0}
                className="flex-1 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 disabled:opacity-40 flex items-center justify-center gap-2">
                Next <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Summary & Suggestions */}
        {step === 3 && (
          <div data-testid="picker-step-3">
            <h2 className="text-xl font-bold text-gray-900 mb-1 text-center">Nice picks. Here&apos;s what we&apos;ll focus on first.</h2>
            <p className="text-sm text-gray-600 text-center mb-6">
              You chose: {selectedTexts.map((t, i) => (
                <span key={i}>{i > 0 ? (i === selectedTexts.length - 1 ? ', and ' : ', ') : ''}<strong>{t.toLowerCase()}</strong></span>
              ))}.
            </p>

            <div className="space-y-4 mb-6">
              {suggestions.map(s => (
                <div key={s.id} className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-gray-900">{s.title}</h3>
                    <div className="flex gap-1">
                      {s.pillars.map(p => (
                        <span key={p} className={`text-[10px] px-2 py-0.5 rounded-full capitalize font-medium ${
                          p === 'health' ? 'bg-pink-100 text-pink-600' :
                          p === 'wealth' ? 'bg-blue-100 text-blue-600' :
                          'bg-amber-100 text-amber-600'
                        }`}>{p}</span>
                      ))}
                      <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full capitalize">{s.skill_area.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                  <ul className="space-y-1 mb-3">
                    {s.steps.slice(0, 3).map((step, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="w-4 h-4 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                        {step}
                      </li>
                    ))}
                    {s.steps.length > 3 && <li className="text-xs text-gray-400 ml-6">+ {s.steps.length - 3} more</li>}
                  </ul>
                  <p className="text-[10px] text-gray-400 mb-3">{s.steps.length} small actions · Doable in about 1-2 weeks</p>
                  <div className="flex gap-2">
                    <button onClick={() => applyPathway(s)} disabled={creating === s.id}
                      className="flex-1 py-2.5 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50 text-sm flex items-center justify-center gap-1">
                      {creating === s.id ? <Loader2 size={14} className="animate-spin" /> : <Target size={14} />} Use this pathway
                    </button>
                    <button onClick={() => applyPathway(s, true)} disabled={creating === s.id}
                      className="py-2.5 px-4 border-2 border-gray-200 text-gray-700 font-medium rounded-xl hover:border-purple-300 text-sm flex items-center gap-1">
                      <Pencil size={14} /> Edit first
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <button onClick={() => navigate('/pathways/create')} className="text-sm text-gray-500 hover:text-purple-600 underline">
                Prefer to design your own from scratch?
              </button>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(2)} className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl">Back</button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FocusPicker;
